import { useQuery } from '@tanstack/react-query';
import { getClientPortalBillingDashboard } from 'services/billingServices';
import { normalizeSubscriptionInvoiceItem, normalizeSubscriptionPaymentRequestItem } from '../../helpers/subscriptionBillingHelpers';

export const SUBSCRIPTION_BILLING_DASHBOARD_QUERY_KEY = 'subscription-billing-dashboard';

const UNPAID_STATUSES = new Set(['unpaid', 'overdue', 'partial']);

const extractItems = (section) => {
  if (Array.isArray(section)) return section;
  if (section && typeof section === 'object' && Array.isArray(section.items)) return section.items;
  return [];
};

const resolveItemStatus = (item) => item?.status ?? item?.payment_request_status ?? item?.invoice_status ?? '';

const isUnpaidStatus = (status) => UNPAID_STATUSES.has(String(status ?? '').toLowerCase());

const countUnpaidItems = (items) => items.filter((item) => isUnpaidStatus(resolveItemStatus(item))).length;

const resolveUnpaidCount = (apiCount, items) => apiCount ?? countUnpaidItems(items);

/**
 * Normalizes subscription dashboard API response to the shape consumed by dashboard content.
 *
 * @param {object} raw - Raw API response body
 * @returns {object} Normalized dashboard data
 */
export const normalizeSubscriptionDashboardResponse = (raw = {}) => {
  const paymentRequests = extractItems(raw.payment_request ?? raw.payment_requests).map(normalizeSubscriptionPaymentRequestItem);
  const invoices = extractItems(raw.invoice ?? raw.invoices).map(normalizeSubscriptionInvoiceItem);

  const sectionsVisible = {
    payment_requests: paymentRequests.length > 0,
    invoices: invoices.length > 0,
  };

  return {
    payment_requests: paymentRequests,
    payment_requests_unpaid_count: resolveUnpaidCount(raw.total_unpaid_payment_requests_count, paymentRequests),
    payment_requests_view_all: raw.view_more_payment_request ?? false,
    invoices,
    invoices_unpaid_count: resolveUnpaidCount(raw.total_unpaid_invoices_count, invoices),
    invoices_view_all: raw.view_more_invoices ?? false,
    sections_visible: sectionsVisible,
  };
};

export const invalidateClientSubscriptionBillingQueries = (queryClient) =>
  queryClient.invalidateQueries({ queryKey: [SUBSCRIPTION_BILLING_DASHBOARD_QUERY_KEY] });

export const useSubscriptionBillingDashboardQuery = ({ tenant_id, tenant_sk, case_id, contact_id, enabled = true }) => {
  return useQuery({
    queryKey: [SUBSCRIPTION_BILLING_DASHBOARD_QUERY_KEY, case_id, contact_id],
    queryFn: async () => {
      const response = await getClientPortalBillingDashboard(tenant_id, tenant_sk, case_id, contact_id, 'subscription');
      return normalizeSubscriptionDashboardResponse(response?.data ?? {});
    },
    enabled: Boolean(enabled && tenant_id && tenant_sk && case_id && contact_id),
    suspense: true,
    useErrorBoundary: true,
    staleTime: 2 * 60 * 1000,
    cacheTime: 5 * 60 * 1000,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  });
};
